import { useQuery } from '@tanstack/react-query';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterOutputs } from '../../api/trpc';

export type PublishStats = RouterOutputs['publish']['stats'];
export type PublishJob = RouterOutputs['publish']['listJobs'][number];
export type PublishJobDetail = RouterOutputs['publish']['getJob'];
export type PublishTask = PublishJobDetail['tasks'][number];
export type PublishTaskField = PublishTask['fields'][number];

const isActive = (status: string): boolean => status === 'queued' || status === 'running';

export function usePublishStats(enabled = true, poll = false) {
  return useQuery(trpc.publish.stats.queryOptions(undefined, { enabled, refetchInterval: poll ? 5000 : false }));
}

export function usePublishJobs(enabled = true) {
  return useQuery(
    trpc.publish.listJobs.queryOptions(undefined, {
      enabled,
      refetchInterval: (query) => (query.state.data?.some((j) => isActive(j.status)) ? 3000 : false),
    }),
  );
}

export function usePublishJob(id: number) {
  return useQuery(
    trpc.publish.getJob.queryOptions(
      { id },
      {
        enabled: Number.isFinite(id) && id > 0,
        refetchInterval: (query) => (isActive(query.state.data?.job.status ?? '') ? 3000 : false),
      },
    ),
  );
}

/** Publish stats/jobs changed — and with them the guided pipeline's view of publishing. */
export function invalidatePublish(): Promise<void> {
  return invalidateAll(trpc.publish.pathFilter(), trpc.pipeline.pathFilter());
}
